import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Switch, 
  FormControlLabel 
} from '@mui/material';
import axios from 'axios';

function DuplicateOrderSettings() {
  const [settings, setSettings] = useState({
    duplicate_detection_enabled: true,
    duplicate_detection_window_hours: 24,
    duplicate_detection_quantity_threshold: 10,
    notification_type: 'warning',
    auto_block_duplicates: false
  });
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Fetch existing duplicate order settings on component mount
  useEffect(() => {
    fetchDuplicateOrderSettings();
  }, []);

  const fetchDuplicateOrderSettings = async () => {
    try {
      const response = await axios.get('/api/duplicate_order_settings.php', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });

      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (err) {
      setError('Failed to load duplicate order settings');
    }
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/duplicate_order_settings.php', settings, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });

      if (response.data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to save duplicate order settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Duplicate Order Detection Settings
      </Typography>

      <Paper elevation={3} style={{ padding: '20px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="duplicate_detection_enabled"
                  checked={settings.duplicate_detection_enabled}
                  onChange={handleSettingsChange}
                  color="primary"
                />
              }
              label="Enable Duplicate Order Detection"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Detection Window (Hours)</InputLabel>
              <Select
                name="duplicate_detection_window_hours"
                value={settings.duplicate_detection_window_hours}
                onChange={handleSettingsChange}
                disabled={!settings.duplicate_detection_enabled}
              >
                {[6, 12, 24, 48, 72].map(hours => (
                  <MenuItem key={hours} value={hours}>
                    {hours} Hours
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Quantity Threshold</InputLabel>
              <Select
                name="duplicate_detection_quantity_threshold"
                value={settings.duplicate_detection_quantity_threshold}
                onChange={handleSettingsChange}
                disabled={!settings.duplicate_detection_enabled}
              >
                {[5, 10, 15, 20, 25].map(qty => (
                  <MenuItem key={qty} value={qty}>
                    {qty} Units
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Notification Type</InputLabel>
              <Select
                name="notification_type"
                value={settings.notification_type}
                onChange={handleSettingsChange}
                disabled={!settings.duplicate_detection_enabled}
              >
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="block">Block Order</MenuItem>
                <MenuItem value="log">Log Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="auto_block_duplicates"
                  checked={settings.auto_block_duplicates}
                  onChange={handleSettingsChange}
                  color="primary"
                  disabled={!settings.duplicate_detection_enabled}
                />
              }
              label="Automatically Block Duplicate Orders"
            />
          </Grid>
        </Grid>

        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSaveSettings}
          disabled={loading || !settings.duplicate_detection_enabled}
          style={{ marginTop: '20px' }}
        >
          {loading ? 'Saving...' : 'Save Duplicate Order Settings'}
        </Button>

        {saveSuccess && (
          <div style={{
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '10px',
            marginTop: '10px',
            borderRadius: '5px'
          }}>
            Duplicate Order Settings saved successfully! 🎉
          </div>
        )}
      </Paper>
    </Container>
  );
}

export default DuplicateOrderSettings;
